/**
 * apps/koordinator-app/src/components/ProtectedRoute.tsx
 * 
 * Now uses the shared auth package through the compatibility layer.
 * Provides backwards-compatible routing protection for koordinator-app.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../shared/contexts';
import { Spinner } from '@bright-web-skeleton/ui';
import { hasFreshOnboardingExit } from '../utils/onboardingExternalRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireOnboarding?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
  requireOnboarding = true,
}) => {
  const { user, loading, isSessionExpired } = useAuth();
  const location = useLocation();

  // CRITICAL: While auth is loading, ALWAYS show spinner - never redirect
  // This prevents the race condition where OAuth callback hasn't been processed yet
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  // If session expired, redirect to login with signedOut param
  if (isSessionExpired) {
    return (
      <Navigate 
        to={`${redirectTo}?signedOut=1`}
        replace 
        state={{ from: location.pathname, sessionExpired: true }} 
      />
    );
  }

  // No user after loading is complete - redirect to login
  if (!user) {
    return (
      <Navigate 
        to={redirectTo} 
        replace 
        state={{ from: location.pathname }} 
      />
    );
  }

  const isOnboardingConnectFlow =
    location.pathname.startsWith('/store')
    && new URLSearchParams(location.search).get('onboarding_return') === 'brand-sales';

  // If onboarding is required and user hasn't onboarded, redirect to onboarding
  // unless the user explicitly chose to exit the wizard or is completing a
  // Sales-step connect flow opened from onboarding.
  if (requireOnboarding && !user.onboarded && !hasFreshOnboardingExit(user.id) && !isOnboardingConnectFlow) {
    return <Navigate to="/onboarding/brand" replace />;
  }

  // NOTE: We intentionally do NOT redirect onboarded users away from /onboarding.
  // An onboarded Brand Manager clicking "Create Brand" must be allowed to reach
  // /onboarding/create-brand-store to create an additional brand.
  // The old guard `if (user.onboarded && pathname.startsWith('/onboarding')) → /dashboard`
  // was removed because it blocked explicit user intent.

  return <>{children}</>;
};
